import { memo, useCallback, useState } from "react";
import { Mic, Square, X, Send, Loader2 } from "lucide-react";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface VoiceRecorderProps {
  chatId: string;
  onMessageSent: () => void;
}

const VoiceRecorder = memo(({ chatId, onMessageSent }: VoiceRecorderProps) => {
  const { user } = useAuthContext();
  const { 
    isRecording, 
    recordingDuration, 
    audioBlob, 
    startRecording, 
    stopRecording, 
    cancelRecording,
    resetRecording 
  } = useAudioRecorder();
  const [isSending, setIsSending] = useState(false);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleStartRecording = useCallback(async () => {
    try {
      await startRecording();
    } catch (error) {
      console.error("Failed to start recording:", error);
    }
  }, [startRecording]);

  const handleSendVoiceMessage = useCallback(async () => {
    if (!audioBlob || !user) return;

    setIsSending(true);
    try {
      // Upload audio to storage
      const fileName = `${user.id}/${Date.now()}.webm`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("voice-messages")
        .upload(fileName, audioBlob, {
          contentType: "audio/webm",
          cacheControl: "3600",
        });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        throw uploadError;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("voice-messages")
        .getPublicUrl(uploadData.path);

      // Send message with audio URL
      const { error: messageError } = await supabase
        .from("messages")
        .insert({
          chat_id: chatId,
          sender_id: user.id,
          content: "🎤 Voice message",
          audio_url: urlData.publicUrl,
        });

      if (messageError) {
        console.error("Message error:", messageError);
        throw messageError;
      }

      resetRecording();
      onMessageSent();
    } catch (error) {
      console.error("Failed to send voice message:", error);
    } finally {
      setIsSending(false);
    }
  }, [audioBlob, user, chatId, resetRecording, onMessageSent]);

  // Show recording UI when recording
  if (isRecording) {
    return (
      <div className="flex items-center gap-3 flex-1">
        <button
          onClick={cancelRecording}
          className="p-2 rounded-full hover:bg-muted transition-colors text-destructive"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex-1 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
          <span className="text-sm text-muted-foreground">
            Recording... {formatDuration(recordingDuration)}
          </span>
        </div>
        
        <button
          onClick={stopRecording}
          className="p-3 rounded-full bg-destructive text-white"
        >
          <Square className="w-5 h-5" />
        </button>
      </div>
    );
  }

  // Show preview UI after recording
  if (audioBlob) {
    return (
      <div className="flex items-center gap-3 flex-1">
        <button
          onClick={resetRecording}
          className="p-2 rounded-full hover:bg-muted transition-colors text-destructive"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex-1 flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            Voice message ready ({formatDuration(recordingDuration)})
          </span>
        </div>
        
        <button
          onClick={handleSendVoiceMessage}
          disabled={isSending}
          className="p-3 rounded-full pop-gradient text-white disabled:opacity-50"
        >
          {isSending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>
    );
  }

  // Show mic button when idle
  return (
    <button
      onClick={handleStartRecording}
      className="p-2 rounded-full hover:bg-muted transition-colors"
      title="Record voice message"
    >
      <Mic className="w-6 h-6" />
    </button>
  );
});

VoiceRecorder.displayName = "VoiceRecorder";

export default VoiceRecorder;
